import { _callBackend } from './api';
import { DEFAULT_OVERVIEW_FILTER } from '@/_constants/overview';
import type { SendQuestionnaireQuestion } from '@/_constants/questionnaires';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { Question } from '@/_types/models/questionnaire';
import type { QuestionnaireIndexSerializer } from '@/_types/serializers/v1/questionnaires';
import type { OverviewFilterObject } from '@/_types/overview';
import type { QuestionnaireTemplate } from '@/_types/models/questionnaireTemplate';

export const getAllQuestionnaireYears = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend(
    '/advisors/get_all_questionnaire_years',
    'GET',
    null,
    token,
  );

export const getScheduledQuestionnaires = async ({
  token,
  show_mine,
  show_active,
}: APIRequest<{
  show_active: boolean;
  show_mine: boolean;
}>): APIResponsePromise =>
  await _callBackend(
    '/template_schedulers/get_all_template_schedulers',
    'POST',
    { show_active, show_mine },
    token,
  );

export const sendDraftQuestionnaire = async ({
  token,
  questionnaire_ids,
}: APIRequest<{ questionnaire_ids: Array<number> }>): APIResponsePromise =>
  await _callBackend(
    '/questionnaires/send_questionnaire_notifications',
    'POST',
    { questionnaire_ids },
    token,
  );

export const getAdminQuestionnaires = async (
  attrs: APIRequest<OverviewFilterObject> = DEFAULT_OVERVIEW_FILTER,
): APIResponsePromise<Array<QuestionnaireIndexSerializer>> => {
  const adjustedPage: number = (attrs.page ?? 0) + 1;

  const selected_client_tags_filter: string =
    attrs.selected_client_tags_filter?.length > 0
      ? '&' +
        attrs.selected_client_tags_filter
          ?.map(
            (tag) => `selected_client_tags_filter[]=${encodeURIComponent(tag)}`,
          )
          .join('&')
      : '';
  const selected_entity_tags_filter: string =
    attrs.selected_entity_tags_filter?.length > 0
      ? '&' +
        attrs.selected_entity_tags_filter
          ?.map(
            (tag) => `selected_entity_tags_filter[]=${encodeURIComponent(tag)}`,
          )
          .join('&')
      : '';

  const selected_firm_custom_status_filter: string =
    attrs.selected_firm_custom_status_filter?.length > 0
      ? `&selected_firm_custom_status_filter[]=${encodeURIComponent(
          attrs.selected_firm_custom_status_filter?.join(','),
        )}`
      : '';

  // prettier-ignore
  return await _callBackend(
    `/v2/questionnaires?year=${attrs.year
    }&page=${adjustedPage
    }&rows_per_page=${attrs.rows_per_page
    }&questionnaire_status=${attrs.questionnaire_status
    }&questionnaire_status_include_locked=${attrs.questionnaire_status_include_locked
    }&questionnaire_status_exclude_completed=${attrs.questionnaire_status_exclude_completed
    }&firm_group_id=${attrs.firm_group_id
    }&sort_by=${attrs.sort_by
    }&descending=${attrs.descending
    }&questionnaire_type=${attrs.questionnaire_type
    }&entity_type=${attrs.entity_type
    }&search_term=${encodeURIComponent(attrs.search_term)
    }&hide_inactives=${attrs.hide_inactives
    }&hide_actives=${attrs.hide_actives
    }&assigned_advisor_id=${attrs.assigned_advisor_id
    }&firm_role_id=${attrs.firm_role_id
    }&firm_role_user_id=${attrs.firm_role_user_id
    }&hide_inactive_users=${attrs.hide_inactive_users
    }&hide_active_users=${attrs.hide_active_users
    }&show_only_deleted=${!!attrs.show_only_deleted
    }${attrs.count ? '&count=true' : ''}${selected_client_tags_filter}${selected_entity_tags_filter}${selected_firm_custom_status_filter
    }`
    ,
    'GET',
    null,
    attrs.token,
  );
};

export interface SendQuestionnaireParams {
  all_owner_signature_required: boolean;
  batch_reminder: boolean;
  batch_sending: boolean;
  company_ids: Array<number>;
  company_types: Array<string>;
  days_until_due: number | null;
  deposit_amount: number | null;
  deposit_required: boolean | null;
  document_drop_off_enabled?: boolean;
  document_popout_checklist_enabled?: boolean;
  due_date: string | null;
  first_scheduled_date: string;
  frequency: string | null;
  include_prior_year_items: boolean;
  is_draft?: boolean;
  questionnaire_template_id: number | null;
  questions_to_create: Array<SendQuestionnaireQuestion>;
  custom_template_name?: string | null;
  custom_email_subject?: string | null;
  custom_email_body?: string | null;
  sections?: Array<string>;
  show_uploads_as_document_checklist?: boolean;
  tax_year: number;
  update_profile_section: boolean;
  custom_receiver_client_role?: string | null;
}

export interface SendQuestionnaireResponse {
  message: string;
}

export const sendQuestionnaires = async ({
  params,
  token,
}: APIRequest<{
  params: SendQuestionnaireParams;
}>): APIResponsePromise<SendQuestionnaireResponse> => {
  // TODO(API): @chris anything here would be considered true on the backend, leave out for non-drafts
  const { is_draft, ...paramsWithoutIsDraft } = params;

  return await _callBackend(
    '/questionnaires/send_questionnaires',
    'POST',
    {
      ...paramsWithoutIsDraft,
      ...(is_draft && { is_draft: true }),
    },
    token,
  );
};

export const getAllQuestionnaireTemplatesInfo = async ({
  token,
}: APIRequest): APIResponsePromise<Array<QuestionnaireTemplate>> =>
  await _callBackend(
    '/questionnaire_templates/get_all_questionnaire_templates_info',
    'GET',
    null,
    token,
  );

export const sendOpenItems = async ({
  params,
  token,
}: APIRequest<{ params: object }>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/send_open_items',
    'POST',
    { ...params },
    token,
  );
};

export interface ExportQuestionnairesAttrs {
  convert_images_to_pdfs: boolean;
  documents_to_exclude: Array<any>;
  documents_to_include: Array<any> | null;
  export_to_platforms: Array<unknown>;
  include_annotations: boolean;
  include_documents: boolean;
  include_pages: boolean;
  include_toc: boolean;
  questionnaire_ids: Array<number>;
  remove_nos_and_na_answers: boolean;
  save_as_name: string | null;
  save_as_names: { [key: number]: string } | null;
  trigger_webhook: boolean;
  zipped: boolean;
  format?: string;
}

export const exportQuestionnaires = async ({
  questionnaire_ids,
  include_annotations,
  include_documents,
  include_toc,
  include_pages,
  remove_nos_and_na_answers,
  convert_images_to_pdfs,
  save_as_name,
  save_as_names,
  trigger_webhook,
  export_to_platforms,
  zipped,
  documents_to_include,
  documents_to_exclude,
  token,
  format,
}: APIRequest<ExportQuestionnairesAttrs>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/export_questionnaires',
    'POST',
    {
      questionnaire_ids,
      include_annotations,
      include_documents,
      include_toc,
      include_pages,
      remove_nos_and_na_answers,
      convert_images_to_pdfs,
      save_as_name,
      save_as_names,
      trigger_webhook,
      export_to_platforms,
      zipped,
      documents_to_include,
      documents_to_exclude,
      format,
    },
    token,
  );
};

export const exportQuestionnaireToSpreadsheet = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
}>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/export_questionnaire_to_spreadsheet',
    'POST',
    { id, company_id: companyId },
    token,
    { returnResponseAsIs: true },
  );
};

export const sendReminders = async ({
  params,
  token,
}: APIRequest<{ params: object }>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/send_questionnaire_reminders',
    'POST',
    { ...params },
    token,
  );
};

export const saveQuestionnaires = async ({
  company_ids,
  reminders_disabled,
  questionnaire_ids,
  reminder_email_subject,
  reminder_email_message,
  reminder_in_app_message,
  reminder_sms_message,
  due_date,
  customize,
  token,
}: APIRequest<{
  company_ids?: Array<number>;
  questionnaire_ids: Array<number>;
  reminders_disabled?: boolean;
  due_date?: string;
  reminder_email_subject?: string;
  reminder_email_message?: string;
  reminder_in_app_message?: string;
  reminder_sms_message?: string;
  customize?: boolean;
}>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/save_questionnaires',
    'POST',
    {
      company_ids,
      reminders_disabled,
      due_date,
      questionnaire_ids,
      reminder_email_subject,
      reminder_email_message,
      reminder_in_app_message,
      reminder_sms_message,
      customize,
    },
    token,
  );
};

export const saveQuestionnaire = async ({
  company_id,
  due_date,
  id,
  firm_custom_status_id,
  reminders_disabled,
  deposit_status,
  token,
}: APIRequest<{
  company_id?: number;
  due_date?: string;
  id: number;
  firm_custom_status_id?: number | null;
  reminders_disabled?: boolean;
  deposit_status?: string;
}>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/save_questionnaire',
    'POST',
    {
      company_id,
      due_date,
      id,
      reminders_disabled,
      firm_custom_status_id,
      deposit_status,
    },
    token,
  );
};

export const deleteQuestionnaire = async ({
  companyId,
  id,
  token,
}: APIRequest<{
  companyId: number;
  id: number;
}>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/delete_questionnaire',
    'POST',
    { company_id: companyId, id },
    token,
  );
};

export const lockQuestionnaires = async ({
  questionnaireIds,
  token,
}: APIRequest<{
  questionnaireIds: number[];
}>): APIResponsePromise => {
  return await _callBackend(
    '/questionnaires/lock_questionnaires',
    'POST',
    { questionnaire_ids: questionnaireIds },
    token,
  );
};

export const adminAnswerQuestion = async ({
  answer,
  company_id,
  answer_by,
  id,
  token,
}: APIRequest<{
  answer: string;
  company_id: number;
  answer_by?: string;
  id: number;
}>): APIResponsePromise<Question> => {
  return await _callBackend(
    '/questionnaires/answer_question',
    'POST',
    { answer, company_id, id, answer_by },
    token,
  );
};

export const sendCustomStatusChange = async ({
  questionnaireIds,
  customEmailSubject,
  customEmailMessage,
  customSmsMessage,
  token,
}: APIRequest<{
  questionnaireIds: Array<number>;
  customEmailSubject: string;
  customEmailMessage: string;
  customSmsMessage?: string;
}>): APIResponsePromise<Question> => {
  return await _callBackend(
    '/questionnaires/send_custom_status_change',
    'POST',
    {
      questionnaire_ids: questionnaireIds,
      custom_email_subject: customEmailSubject,
      custom_email_message: customEmailMessage,
      custom_sms_message: customSmsMessage,
    },
    token,
  );
};

export const submitQuestionnaire = async ({
  id,
  companyId,
  token,
}: APIRequest<{ id: number; companyId: number }>): APIResponsePromise => {
  return await _callBackend(
    `/questionnaires/${id}/submit_questionnaire`,
    'POST',
    { company_id: companyId },
    token,
  );
};

export const markQuestionnaireCompleted = async ({
  id,
  companyId,
  token,
}: APIRequest<{ id: number; companyId: number }>): APIResponsePromise => {
  return await _callBackend(
    `/questionnaires/${id}/mark_completed`,
    'POST',
    { company_id: companyId },
    token,
  );
};

export const unsubmitQuestionnaire = async ({
  id,
  companyId,
  token,
}: APIRequest<{ id: number; companyId: number }>): APIResponsePromise => {
  return await _callBackend(
    `/questionnaires/${id}/unsubmit_questionnaire`,
    'POST',
    { company_id: companyId },
    token,
  );
};

export const getSentQuestionnairesUsageDataForTaxYear = async ({
  entityType,
  taxYear,
  token,
}: APIRequest<{
  entityType?: string;
  taxYear: number;
}>): APIResponsePromise => {
  return await _callBackend(
    `/v2/questionnaires/get_sent_questionnaires_usage_data_for_tax_year?tax_year=${
      taxYear
    }&entity_type=${entityType}`,
    'GET',
    null,
    token,
  );
};

export const restoreDeletedQuestionnaire = async ({
  questionnaire_id,
  token,
}: {
  questionnaire_id: number;
  token: string;
}): APIResponsePromise<{ success: boolean }> => {
  return await _callBackend(
    `/v2/questionnaires/${questionnaire_id}/restore_deleted_questionnaire`,
    'POST',
    null,
    token,
  );
};
